import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Accessibility, ShoppingCart, Building2, Phone } from 'lucide-react';

const STORAGE_KEY = 'ga_intent_popup_seen';

// Gestos Ágeis palette — green brand + gold accent (matches OrthocareHeroSearch).
const BRAND = '#0e6b4f';
const BRAND_DARK = '#0a4a37';
const GOLD = '#c5a059';
const GOLD_DARK = '#a07e3c';

const options = [
  { key: 'rent', label: 'Quero Alugar', desc: 'Equipamento ortopédico para alugar', icon: Accessibility, path: '/start', primary: true },
  { key: 'buy', label: 'Quero Comprar', desc: 'Loja online de material ortopédico', icon: ShoppingCart, path: '/Shop', accent: GOLD, accentDark: GOLD_DARK },
  { key: 'company', label: 'Site da Empresa', desc: 'Conhecer a Gestos Ágeis', icon: Building2, path: '/LandingPage', accent: BRAND, accentDark: BRAND_DARK },
  { key: 'contact', label: 'Contactos das Lojas', desc: 'Falar connosco / visitas', icon: Phone, path: '/contact', accent: BRAND_DARK, accentDark: '#063027' },
];

export default function GestosAgeisIntentPopup() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // This popup lives on the Gestos Ágeis landing (/gestosageis), which is the
    // entry page for gestosageis.pt. Show once per session.
    if (window.location.pathname !== '/gestosageis') return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, []);

  const handleChoose = (path) => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
    navigate(path);
  };

  const handleClose = () => {
    sessionStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent aria-label="Gestos Ágeis — Como podemos ajudá-lo" className="max-w-lg p-0 overflow-hidden rounded-3xl border-0 shadow-2xl [&>button]:text-white [&>button]:hover:bg-white/10 [&>button]:hover:opacity-100">
        {/* Header */}
        <div className="relative px-6 pt-7 pb-8 text-white" style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }}>
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase mb-2.5" style={{ color: GOLD }}>
            <span className="w-5 h-px" style={{ background: GOLD }} />Gestos Ágeis<span className="w-5 h-px" style={{ background: GOLD }} />
          </span>
          <h2 className="text-2xl font-black leading-tight">Bem-vindo à Gestos Ágeis</h2>
          <p className="text-sm text-white/85 mt-1.5">Como podemos ajudá-lo hoje?</p>
        </div>

        {/* Cards */}
        <div className="p-5 grid grid-cols-2 gap-3 bg-white">
          {options.map((opt) => {
            const Icon = opt.icon;
            if (opt.primary) {
              return (
                <button
                  key={opt.key}
                  onClick={handleClose}
                  className="group flex flex-col items-start gap-2.5 p-4 rounded-2xl text-left text-white transition-all hover:-translate-y-0.5 hover:shadow-xl shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` }}
                >
                  <span className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <span className="font-bold text-sm leading-tight">{opt.label}</span>
                  <span className="text-xs text-white/80 leading-tight">{opt.desc}</span>
                </button>
              );
            }
            return (
              <button
                key={opt.key}
                onClick={() => handleChoose(opt.path)}
                className="group flex flex-col items-start gap-2.5 p-4 rounded-2xl bg-white border-2 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                style={{ borderColor: `${opt.accent}55` }}
              >
                <span className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors group-hover:scale-105" style={{ backgroundColor: `${opt.accent}1a` }}>
                  <Icon className="w-5 h-5" style={{ color: opt.accentDark }} />
                </span>
                <span className="font-bold text-sm leading-tight text-slate-800">{opt.label}</span>
                <span className="text-xs text-slate-500 leading-tight">{opt.desc}</span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 -mt-1 bg-white">
          <button onClick={handleClose} className="text-xs text-slate-500 hover:text-slate-700 w-full text-center font-medium transition-colors">
            Continuar a navegar nesta página
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}